/**
 * State Name Mapping Utilities
 *
 * Converts full state names from API ("New York") to state codes ("NY")
 * and FIPS codes for SVG map rendering.
 */
import { STATE_NAMES, STATE_TO_FIPS, type USStateCode } from '../types/election.types';

/**
 * Reverse lookup: Full state name -> State code
 * Built from existing STATE_NAMES constant to avoid duplication
 */
const STATE_NAME_TO_CODE: Record<string, USStateCode> = Object.entries(STATE_NAMES).reduce(
  (acc, [code, name]) => {
    acc[name] = code as USStateCode;
    return acc;
  },
  {} as Record<string, USStateCode>,
);

/**
 * Convert full state name to state code
 *
 * @param name - Full state name from API (e.g., "New York", "North Carolina")
 * @returns State code (e.g., "NY", "NC") or null if not found
 *
 * @example
 * stateNameToCode("New York") // "NY"
 * stateNameToCode("District of Columbia") // "DC"
 * stateNameToCode("Invalid") // null
 */
export function stateNameToCode(name: string): USStateCode | null {
  return STATE_NAME_TO_CODE[name] ?? null;
}

/**
 * Convert full state name directly to FIPS code
 *
 * @param name - Full state name from API
 * @returns FIPS code (e.g., "36" for NY) or null if not found
 *
 * @example
 * stateNameToFips("New York") // "36"
 * stateNameToFips("California") // "06"
 */
export function stateNameToFips(name: string): string | null {
  const stateCode = stateNameToCode(name);
  if (!stateCode) return null;
  return STATE_TO_FIPS[stateCode] ?? null;
}
